import styled, { css } from 'styled-components';
import theme from '../theme';

export const SidebarOverlay = styled.div`
  display: none;

  ${theme.media.tablet} {
    display: ${({ $open }) => $open ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

export const SidebarWrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${({ $collapsed }) => $collapsed ? theme.layout.sidebarCollapsed : theme.layout.sidebarWidth};
  background: ${theme.colors.sidebarBg};
  display: flex;
  flex-direction: column;
  transition: width ${theme.transitions.normal}, transform ${theme.transitions.normal};
  z-index: 100;
  overflow: hidden;

  ${theme.media.tablet} {
    width: ${theme.layout.sidebarWidth};
    transform: ${({ $mobileOpen }) => $mobileOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

export const LogoArea = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 78px;
  flex-shrink: 0;
  border-bottom: 1px solid ${theme.colors.sidebarDivider};
  overflow: visible;
`;

export const LogoImage = styled.img`
  max-width: 136px;
  height: auto;
  transition: opacity ${theme.transitions.fast};
`;

export const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${theme.colors.primary};
  border-radius: ${theme.radii.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 16px;
  font-family: ${theme.typography.fontFamily};
  flex-shrink: 0;
`;

export const NavSectionWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  min-height: 0;
`;

export const NavSection = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: ${theme.spacing.sm} 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.sidebarDivider};
    border-radius: ${theme.radii.full};
  }
`;

export const ScrollButton = styled.button`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border: none;
  cursor: pointer;
  font-size: 11px;
  font-family: ${theme.typography.fontFamily};
  font-weight: 600;
  letter-spacing: 0.5px;
  color: ${theme.colors.sidebarTextActive};
  transition: opacity ${theme.transitions.fast}, background ${theme.transitions.fast};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  pointer-events: ${({ $visible }) => $visible ? 'auto' : 'none'};

  ${({ $position }) => $position === 'top' && css`
    top: 0;
    background: linear-gradient(to bottom, ${theme.colors.sidebarBg} 60%, rgba(15, 43, 61, 0.85));
  `}

  ${({ $position }) => $position === 'bottom' && css`
    bottom: 0;
    background: linear-gradient(to top, ${theme.colors.sidebarBg} 60%, rgba(15, 43, 61, 0.85));
  `}

  &:hover {
    color: #ffffff;
    background: ${theme.colors.sidebarHover};
  }
`;

export const NavGroup = styled.div`
  margin-bottom: ${theme.spacing.xs};
`;

export const NavGroupLabel = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-size: ${theme.typography.tiny.size};
  font-weight: 600;
  color: ${theme.colors.sidebarText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 0.6};
  transition: opacity ${theme.transitions.fast};
  white-space: nowrap;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.sidebarDivider};
  margin: ${theme.spacing.sm} ${theme.spacing.lg};
`;

export const NavItem = styled.a`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  margin: 2px ${theme.spacing.sm};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.sidebarText};
  text-decoration: none;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  white-space: nowrap;
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  font-family: ${theme.typography.fontFamily};
  gap: ${theme.spacing.md};

  ${({ $collapsed }) => $collapsed && css`
    justify-content: center;
    padding: ${theme.spacing.sm};
    margin: 2px ${theme.spacing.xs};
  `}

  &:hover {
    background: ${theme.colors.sidebarHover};
    color: ${theme.colors.sidebarTextActive};
  }

  ${({ $active }) => $active && css`
    background: ${theme.colors.sidebarActive};
    color: ${theme.colors.sidebarTextActive};

    &:hover {
      background: ${theme.colors.sidebarActive};
    }
  `}
`;

export const NavIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
`;

export const NavLabel = styled.span`
  opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
  transition: opacity ${theme.transitions.fast};
  overflow: hidden;
`;

export const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md};
  margin: ${theme.spacing.sm};
  border: none;
  background: transparent;
  color: ${theme.colors.sidebarText};
  cursor: pointer;
  border-radius: ${theme.radii.sm};
  transition: all ${theme.transitions.fast};
  font-size: 16px;
  flex-shrink: 0;

  &:hover {
    background: ${theme.colors.sidebarHover};
    color: ${theme.colors.sidebarTextActive};
  }

  ${theme.media.tablet} {
    display: none;
  }
`;
